import _ from "lodash";
import prettyMilliseconds from "pretty-ms";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { GroupsRounded } from "@mui/icons-material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Box, Checkbox, CircularProgress, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { blueGrey } from "@mui/material/colors";
import List from "@mui/material/List";
import { DataGridPro, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";

import { CampaignAnalyticsService } from "@analytics/index";

import CompanyDetailsDrawer from "@components/CompanyDetailsDrawer/CompanyDetailsDrawer";
import CompanyDisplay from "@components/CompanyDetailsDrawer/CompanyDisplay";
import CustomLoadingOverlay from "@components/CustomLoadingOverlay";
import ColumnHeader from "@components/Table/ColumnHeader";
import { getRowClassName, tableStyles } from "@components/Table/tableStyles";
import {
  TableToolbar,
  TableToolbarDropdown,
  TableToolbarDropdownMenuItem,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarSearch,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";

import useCounts from "@hooks/useCounts";
import useEmailFilters from "@hooks/useEmailFilters";

import { CampaignType, EmailType, OutcomeType } from "@interfaces/campaign.type";
import { ContactType } from "@interfaces/contact.type";

import DetailsCell from "@pages/Campaigns/components/DetailsCell";
import InteractionsCell from "@pages/Campaigns/components/InteractionsCell";
import OutcomeCell from "@pages/Campaigns/components/OutcomeCell";
import SidebarListItem from "@pages/Campaigns/components/SidebarListItem";
import { exportCampaignContacts } from "@pages/Campaigns/utils";

import { useCreateCampaignQuery, useSendEmailQuery, useUpdateEmailQuery } from "@queries/campaign.query";
import { useCompanyQuery } from "@queries/company.query";

import useCampaignEmailStore from "@store/campaignStore";
import { PopupActions } from "@store/popupStore";

interface ICampaignContactsEmailsProperties {
  campaign: CampaignType;
  emails: EmailType[];
  technologyId: string;
}

interface IContactEmailsRow {
  company: {
    alt: string;
    id: string;
    name: string;
    url: string;
  };
  details: {
    email: string;
    linkedinUrl: string;
    name: string;
  };
  failedToSend: boolean;
  id: number;
  interactions: {
    bounced: boolean;
    clicked: number;
    lead: boolean;
    opened: number;
    responded: number;
  };
  lead: boolean;
  outcome: null | OutcomeType;
  sendAt: null | number;
}

export default function CampaignContactsEmails({ campaign, emails, technologyId }: ICampaignContactsEmailsProperties) {
  const [focusedId, setFocusedId] = useState<null | string>(null);
  const [searchParameters, setSearchParameters] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(searchParameters.get("searchQuery") || "");
  const [filteredRows, setFilteredRows] = useState<EmailType[]>(emails);
  const theme = useTheme();

  const { emailList, selectedEmailList, setEmailList, setSelectedEmailList } = useCampaignEmailStore();

  const { mutateAsync: createCampaign } = useCreateCampaignQuery();

  const handleCreateCampaign = () => {
    if (selectedEmailList.length === 0) {
      PopupActions.openAlert({
        message: "Please select at least one contact to create a campaign",
        severity: "error",
      });
      return;
    }

    const contactIds = selectedEmailList.map((id) => emails.find((email) => email.id === id)!.contactId);

    void createCampaign({
      contactIds,
      technologyId,
    }).then(() => {
      CampaignAnalyticsService.createCampaign(technologyId, contactIds.length);
    });
  };

  const metricCounts = useCounts(emails);

  const urlParameters = useParams();

  const { mutateAsync: sendEmail, status: isEmailSendingStatus } = useSendEmailQuery();

  useEffect(() => {
    if (searchQuery) {
      setSearchParameters((previousParameters) => {
        const newParameters = new URLSearchParams(previousParameters);
        newParameters.set("searchQuery", searchQuery);
        return newParameters;
      });
    } else {
      setSearchParameters((previousParameters) => {
        const newParameters = new URLSearchParams(previousParameters);
        newParameters.delete("searchQuery");
        return newParameters;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters.get("searchQuery"), searchQuery]);

  useEffect(() => {
    setSearchParameters((previousParameters) => {
      const newParameters = new URLSearchParams(previousParameters);
      const section = metricCounts.find((item) => item.key === searchParameters.get("section"));
      newParameters.set("section", section?.key ?? "all");
      return newParameters;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParameters.get("section"), metricCounts]);

  const { data: focusedCompany, fetchCompany, status: companyLoadStatus } = useCompanyQuery();
  const { mutateAsync: updateEmail } = useUpdateEmailQuery();

  const filterRows = useEmailFilters(emails, searchQuery);

  useEffect(() => {
    setEmailList(emails);
  }, [emails, setEmailList]);

  useEffect(() => {
    if (focusedId) {
      fetchCompany(focusedId);
    }
  }, [fetchCompany, focusedId]);

  useEffect(() => {
    const selectedIndex = metricCounts.findIndex((count) => count.key === searchParameters.get("section")) ?? 0;
    const filtered = filterRows(selectedIndex);

    const filteredOutcomeValues = filtered.map((email) => {
      return { ...email, outcome: email.outcome ?? ("" as OutcomeType) };
    });

    setFilteredRows(filteredOutcomeValues);
  }, [emails, searchQuery, filterRows, metricCounts, searchParameters]);

  const handleListItemClick = (_: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    setSearchParameters((previousParameters) => {
      const newParameters = new URLSearchParams(previousParameters);
      newParameters.set("section", metricCounts[index]?.key ?? "all");
      return newParameters;
    });
  };

  const handleOutcomeChange = async (emailId: number, newValue: OutcomeType) => {
    const updatedEmail = emailList.find((email: EmailType) => email.id === emailId);
    if (updatedEmail) {
      try {
        await updateEmail({
          campaignId: updatedEmail.campaignId,
          email: {
            outcome: newValue,
            status: updatedEmail.status,
          },
          emailId: updatedEmail.id,
        });
        CampaignAnalyticsService.outcomeUpdated(newValue ?? "", emailId);
        PopupActions.openAlert({
          message: "Contact updated",
          severity: "success",
        });
      } catch {
        PopupActions.openAlert({
          message: "Failed to update contact",
          severity: "error",
        });
      }
    }
  };

  const handleLeadChange = async (emailId: number, checked: boolean) => {
    const updatedEmail = emailList.find((email: EmailType) => email.id === emailId);
    if (updatedEmail) {
      try {
        await updateEmail({
          campaignId: updatedEmail.campaignId,
          email: {
            outcome: updatedEmail.outcome,
            status: {
              ...updatedEmail.status,
              lead: checked,
            },
          },
          emailId: updatedEmail.id,
        });
        PopupActions.openAlert({
          message: "Contact updated",
          severity: "success",
        });
        if (checked) {
          CampaignAnalyticsService.leadAdded();
        } else {
          CampaignAnalyticsService.leadRemoved();
        }
      } catch {
        PopupActions.openAlert({
          message: "Failed to update contact",
          severity: "error",
        });
      }
    }
  };

  const handleCopyEmail = (email: string) => {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        PopupActions.openAlert({
          message: "Email copied to clipboard",
          severity: "info",
        });
      })
      .catch((error) => {
        PopupActions.openAlert({
          message: "Failed to copy email to clipboard",
          severity: "error",
        });
      });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const columns: GridColDef[] = [
    {
      field: "interactions",
      renderCell: (parameters: GridRenderCellParams<IContactEmailsRow>) => {
        const sendIn = (parameters.row.sendAt || 0) - Date.now();
        if (parameters.row.sendAt && sendIn > 0) {
          return (
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 1,
                justifyContent: "center",
              }}
            >
              <Typography color="info" fontSize={12} fontWeight={500}>
                Scheduled to send in:
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={700}
                sx={{
                  color: theme.palette.success.main,
                }}
              >
                {prettyMilliseconds(sendIn, {
                  compact: true,
                  formatSubMilliseconds: true,
                  secondsDecimalDigits: 0,
                })}
              </Typography>
            </Box>
          );
        }
        if (parameters.row.failedToSend) {
          return (
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 1,
                justifyContent: "center",
              }}
            >
              <Typography color="error" fontSize={12} fontWeight={700}>
                Failed to send
              </Typography>
              <Button
                color="info"
                onClick={() => {
                  if (!urlParameters.campaignId || isEmailSendingStatus === "pending") return;
                  void sendEmail({
                    emailId: parameters.row.id,
                    subject: parameters.row.details.email,
                  });
                }}
                size="small"
                variant="contained"
              >
                {isEmailSendingStatus === "pending" ? (
                  <CircularProgress size={20} sx={{ color: theme.palette.common.white }} />
                ) : (
                  "Resend"
                )}
              </Button>
            </Box>
          );
        }
        return <InteractionsCell interactions={parameters.row.interactions} theme={theme} />;
      },
      renderHeader: () => <ColumnHeader title="Interactions" tooltip="" />,
      sortable: false,
      width: 205,
    },
    {
      field: "details",
      flex: 0.5,
      renderCell: (parameters: GridRenderCellParams<IContactEmailsRow>) => {
        return <DetailsCell details={parameters.row.details} handleCopyEmail={handleCopyEmail} theme={theme} />;
      },
      renderHeader: () => <ColumnHeader title="Details" tooltip="" />,
      sortable: false,
    },
    {
      field: "outcome",
      renderCell: (parameters: GridRenderCellParams<IContactEmailsRow>) => {
        return (
          <OutcomeCell
            handleOutcomeChange={handleOutcomeChange}
            id={parameters.row.id}
            isDisabled={parameters.row.interactions.bounced}
            outcome={parameters.row.outcome}
          />
        );
      },
      renderHeader: () => <ColumnHeader title="Outcome" tooltip="Outcome of the email" />,
      sortable: false,
      width: 200,
    },
    {
      field: "Lead",
      renderCell: (parameters: GridRenderCellParams<IContactEmailsRow>) => (
        <Checkbox
          checked={parameters.row.lead}
          inputProps={{ "aria-label": "Lead checkbox" }}
          onChange={(event) => handleLeadChange(parameters.row.id, event.target.checked)}
        />
      ),
      renderHeader: () => <ColumnHeader title="Lead" tooltip="Lead status" />,
      sortable: false,
      width: 100,
    },
    {
      field: "company",
      flex: 2,
      renderCell: (parameters: GridRenderCellParams<IContactEmailsRow>) => (
        <Box alignItems="center" display="flex" gap={1} onClick={() => setFocusedId(parameters.row.company.id)}>
          <Box
            alt={`${parameters.row.company.alt} logo`}
            component="img"
            onClick={() => setFocusedId(parameters.row.company.id)}
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
            src={parameters.row.company.url}
            sx={{ maxHeight: "51px", maxWidth: "51px", width: "auto" }}
          />
          <Typography>{parameters.row.company.name}</Typography>
        </Box>
      ),
      renderHeader: () => <ColumnHeader title="Company" tooltip="" />,
      sortable: false,
    },
  ];

  const rows: IContactEmailsRow[] = useMemo(
    () =>
      filteredRows.map((email: EmailType) => ({
        company: {
          alt: email.company?.name ?? "",
          id: email.company?.id ?? "",
          name: email.company?.name ?? "",
          url: email.company?.logoUrl ?? "",
        },
        details: {
          email: email.email,
          linkedinUrl: email.linkedinUrl ?? "",
          name: email.name ?? "",
        },
        failedToSend: email.failedToSend,
        id: email.id,
        interactions: {
          bounced: email.status.bounced,
          clicked: email.status.clicked,
          lead: email.status.lead,
          opened: email.status.opened,
          responded: email.status.responded,
        },
        lead: email.status.lead,
        outcome: email.outcome,
        sendAt: email.sendAt,
      })),
    [filteredRows],
  );

  //TODO: Migrate sorting to backend
  const sortedArray = useMemo(() => {
    const groupedByCompany = _.groupBy(rows, "company.name");

    const sortedGroups = _.mapValues(groupedByCompany, (group) => {
      return _.orderBy(
        group,
        (person) => {
          return person.details.name.split(" ").at(-1);
        },
        ["asc"],
      );
    });

    const sortedCompanyNames = _.sortBy(Object.keys(groupedByCompany));
    return _.flatMap(sortedCompanyNames, (company) => sortedGroups[company]);
  }, [filteredRows]);

  const handleSetSelectedEmails = (newSelection: number[]) => {
    setSelectedEmailList(newSelection);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        height: "calc(100vh - 306px)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
          overflow: "auto",
          width: "100%",
        }}
      >
        <CompanyDetailsDrawer
          close={() => {
            setFocusedId(null);
          }}
          isOpen={focusedId !== null && companyLoadStatus === "success"}
        >
          {focusedCompany && (
            <CompanyDisplay
              close={() => {
                setFocusedId(null);
              }}
              company={focusedCompany}
            />
          )}
        </CompanyDetailsDrawer>
        <TableToolbar>
          <TableToolbarLeft>
            <TableToolbarTitle
              resourceLink=""
              tableSubtitle="Manage your email Campaigns and track their performance."
              tableTitle="Campaigns"
              workflow="portfolio"
            />
          </TableToolbarLeft>
          <TableToolbarRight>
            <TableToolbarSearch placeholder="Search Contacts" query={searchQuery} setQuery={handleSearch} />
            <TableToolbarDropdown
              actionsLabel="Actions"
              selectedIds={selectedEmailList}
              tooltipText="Export selected campaigns to CSV."
            >
              <TableToolbarDropdownMenuItem
                onClick={async () => {
                  if (!campaign) return;

                  const selectedContacts = emails
                    .filter((email) => selectedEmailList.includes(email.id))
                    .map((email) => ({
                      ...email,
                      id: email.contactId,
                    }));
                  const selectedContactsIds = selectedContacts.map((contact) => contact.contactId);

                  await exportCampaignContacts(selectedContactsIds, selectedContacts as unknown as ContactType[], [
                    campaign,
                  ]);
                }}
              >
                <ListItemIcon>
                  <GroupsRounded sx={{ color: theme.palette.grey[700] }} />
                </ListItemIcon>
                <ListItemText>Export Contacts</ListItemText>
              </TableToolbarDropdownMenuItem>
              <TableToolbarDropdownMenuItem onClick={handleCreateCampaign}>
                <ListItemIcon>
                  <SendRoundedIcon sx={{ color: theme.palette.grey[700] }} />
                </ListItemIcon>
                <ListItemText>Create New Campaign</ListItemText>
              </TableToolbarDropdownMenuItem>
            </TableToolbarDropdown>
          </TableToolbarRight>
        </TableToolbar>
        <Box
          sx={{
            border: `1px solid ${blueGrey[100]}`,
            display: "flex",
            flexDirection: "row",
            height: "calc(100vh - 274px)",
          }}
        >
          <Box sx={{ bgcolor: `${blueGrey[50]}`, maxWidth: 300, width: "100%" }}>
            <List aria-label="main mailbox folders" component="nav" sx={{ p: 0 }}>
              {metricCounts.map((item, index) => (
                <SidebarListItem
                  count={item.count}
                  handleListItemClick={handleListItemClick}
                  icon={item.icon}
                  index={index}
                  key={index}
                  selectedIndex={metricCounts.findIndex((count) => count.key === searchParameters.get("section")) ?? 0}
                  text={item.text}
                />
              ))}
            </List>
          </Box>
          <Box sx={{ width: "100%" }}>
            <DataGridPro
              checkboxSelection
              columns={columns}
              disableRowSelectionOnClick
              getEstimatedRowHeight={() => 60}
              getRowClassName={getRowClassName}
              getRowHeight={() => "auto"}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 100 } },
              }}
              localeText={{
                noRowsLabel: "No Results Found. Try updating your search term or filters.",
              }}
              onRowSelectionModelChange={(newSelection) =>
                handleSetSelectedEmails(newSelection.map((selection) => +selection))
              }
              pageSizeOptions={[50, 100, 250, 500]}
              pagination
              rows={sortedArray}
              slots={{
                loadingOverlay: CustomLoadingOverlay,
              }}
              sx={{
                ...tableStyles.root,
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "rgb(255, 255, 255)",
                },
                "& .MuiDataGrid-virtualScroller": {
                  height: "calc(100vh - 335px)",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
